// refactoring work in progress

import axios from 'axios';

export enum AppInCloudRoles {
    CompanyAdmin = 'CompanyAdmin',
    Operator = 'Operator'
}

export interface GetLicensesResult {
    oid: string
    companyOid: string
    code: string
    type: string
    description: string
    slots: number
    validityStartDate: string
    validityEndDate: string
    creationDate: string
    lastUpdateDate: string
    enabled: boolean
}

export const GetLicenses = ({ params }: { params: { companyOid: string } }) =>
    axios.get<GetLicensesResult>(`/activations/companies/${params.companyOid}/license`);

export interface UpsertLicensesRequest {
    params: {
        companyOid: string
    }
    data: {
        type: string
        code: string
        description: string
        slots: number
        validityStartDate: string
        validityEndDate: string
    }
}

export const UpsertLicenses = ({ params, data }: UpsertLicensesRequest) =>
    axios.post<{ oid: string }>(`/activations/companies/${params.companyOid}/license`, data);

export const ActivateUserByRole = ({ params, data }: { params: { companyOid: string }, data: { roleOid: string, email: string } }) =>
    axios.post(`/activations/company/${params.companyOid}/activateuserbyrole`, data);

export const UpdateUserByRole = ({ params, data }: { params: { activationOid: string }, data: { roleId: string, enabled: boolean } }) =>
    axios.put(`/activations/${params.activationOid}`, data);

export interface GetUserRolesResult {
    roles: {
        oid: string
        name: AppInCloudRoles
    }[]
}

export const GetUserRoles = () =>
    axios.get<GetUserRolesResult>(`/activations/configurations/roles`);

export interface GetActivationUsersResult {
    users: {
        oid: string
        name: string
        surname: string
        email: string
        creationDate: string
        updateDate: string
        enabled: boolean
        activationOid: string
        roleOid: string
        roleName: AppInCloudRoles
        externalUsername: string
        lastLogin: string
    }[]
}

export const GetActivationUsers = ({ params }: { params: { id: string } }) =>
    axios.get<GetActivationUsersResult>(`/activations/${params.id}/users`);

export const DeleteActivationUser = ({ params }: { params: { id: string } }) =>
    axios.delete(`/activations/${params.id}`);

export interface GetUserDataResponse {
    email: string;
    name: string;
    surname: string;
}

export const GetUserData = () =>
    axios.get<GetUserDataResponse>(`/activations/user`);

export const VerifyUser = () =>
    axios.put(`/activations/verifyuser`);

interface GetCountriesRequest {
    params: {
        orderByPropertyName: 'Description';
        orderByDescending: boolean;
    }
}
export interface CountryDTO {
    blackListDate: string,
    crtVatCode: string,
    description: string,
    enabled: boolean,
    endDate: string,
    euDate: string,
    imagePath: string,
    indType: number,
    iso2Code: string,
    iso3Code: string,
    isoDescription: string,
    note: string,
    oid: string,
    startDate: string,
    updateDate: string
}
interface GetCountriesResponse {
    countries: CountryDTO[]
}

/**
 * Get countries list
 * @param {GetCountriesRequest} request - The request object containing the filters and pagination options
 */
export const GetCountries = (request: GetCountriesRequest) =>
    axios.request<GetCountriesResponse>({
        method: 'GET',
        url: `/company/getCountries`,
        ...request
    });

interface GetAdministrativeDivisionsRequest {
    params: {
        oid: string;
        orderByPropertyName: 'Description';
        orderByDescending: boolean;
    }
}
export interface AdministrativeItemDTO {
    description: string,
    enabled: boolean,
    endDate: string | null,
    imagePath: string | null,
    indType: number,
    isoCode: string,
    isoDescription: string | null,
    notes: string | null,
    oid: string,
    parentDivisionOid: string,
    startDate: string | null,
    typeDescription: string,
    updateDate: string | null
}

interface GetAdministrativeDivisionsResponse {
    administrativeDivisions: AdministrativeItemDTO[]
}

/**
 * Get administrative divisions list
 * @param {GetAdministrativeDivisionsRequest} request - The request object containing the filters and pagination options
 */
export const GetAdministrativeDivisions = (request: GetAdministrativeDivisionsRequest) =>
    axios.request<GetAdministrativeDivisionsResponse>({
        method: 'GET',
        url: `/company/getAdministrativeDivisions`,
        ...request
    });


interface GetCompanyRequest {
    query: {
        oid: string;
    }
}


export interface CompanyBaseDTO {
    oid: string
    serverOid?: string
    serverName?: string
    name: string
    surname: string
    vatCode: string
    fiscalCode: string
    creationDate: string
    usersLinked: boolean
    enabled: boolean
}
export interface CompanyDTO extends CompanyBaseDTO {
    oid: string;
    prefix: string;
    lastName: string
    isCompany: boolean
    description: string
    cciaaRea: string
    phoneNumber: string
    faxNumber: string
    email: string
    pec: string
    region: string
    clientCode: string
    province: string
    city: string
    postalCode: string
    address: string
    name: string;
    /**
     * @default IT isocode
     */
    country: string;
}

/**
 * Get the details of a specific company by its ID
 * @param {GetCompanyRequest} request - The request object containing the company ID
 */
export const GetCompany = (request: GetCompanyRequest) =>
    axios.request<CompanyDTO>({
        method: 'GET',
        url: `/company/GetCompany`,
        params: request.query,
        ...request
    });

interface GetUserLoginsRequest {
    query: {
        oid: string;
    };
    params: {
        orderByPropertyName: string;
        orderByDescending: boolean;
    };
}

interface GetUserLoginsResponse {
    userLogins: string[];
}

/**
 * Get login history of a user
 * @param {GetUserLoginsRequest} request - User id and sorting options
 */
export const GetUserLogins = (request: GetUserLoginsRequest) =>
    axios.request<GetUserLoginsResponse>({
        method: 'GET',
        url: `company/users/${request.query.oid}/logins`,
        ...request
    });